import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SuggestedIcon } from "components/ArtificialIntelligence/SuggestedIcon";
import { unrecognizedOption } from "components/Forms/unrecognizedOption";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import { KeyboardEvent, ReactNode, memo } from "react";
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
} from "react-hook-form";
import { FieldWarningIndicator } from "routes/Customer/Waste/Manifests/FieldWarningIndicator";

import {
  OfferedValues,
  REVEAL_SHORTCUTS_ON_FOCUS,
  offersDifferingFrom,
  takeOfferForEvent,
  useAcknowledgement,
  useArrival,
  useAttentionTarget,
  useAttentionTravel,
} from "../ReviewInteraction";
import {
  ExtractionReviewFormData,
  displayValue,
  isFlagged,
  isRecordConflict,
  isReviewed,
} from "./formData";
import {
  ExtractionDraftValue,
  ExtractionFieldSpec,
  TRI_BOOLEAN_OPTIONS,
  isUnreadDraft,
} from "./types";

/**
 * How sure the extractor was, stated plainly, for hovering a value it produced.
 *
 * Neutral where `lowConfidenceTooltip` is a warning: this hangs off a value on
 * offer, not off a flag, and most of the values it describes are ones the
 * extractor was perfectly confident about. Undefined when it reported no score —
 * there is nothing to say, and saying "no confidence" would read as low
 * confidence.
 */
/**
 * Where a reading came from, and how sure the model was of it.
 *
 * Provenance first, because that is the question the label is answering — "This
 * scan" is short enough to need it spelling out. The score is appended rather
 * than standing alone: on its own a percentage says nothing about whose value it
 * describes.
 */
function readingHint(score: number | null): string {
  const source = "What the AI read from this document, before anyone edited it.";
  const hint = confidenceHint(score);
  return hint == null ? source : `${source} ${hint}`;
}

function confidenceHint(score: number | null): string | undefined {
  if (score == null) return undefined;
  return `The AI was ${Math.round(score * 100)}% confident reading this.`;
}

function lowConfidenceTooltip(score: number | null): string {
  if (score == null) return "The extractor was not confident about this value — please verify it.";
  return `The extractor was ${Math.round(score * 100)}% confident about this value — please verify it.`;
}

/** The same sentence, folded into the disagreement tooltip when both apply. */
function lowConfidenceNote(score: number | null): string {
  if (score == null) return "The extractor was not confident about its reading.";
  return `The extractor was ${Math.round(score * 100)}% confident about its reading.`;
}

export interface ExtractionFieldControlProps {
  spec: ExtractionFieldSpec;
  control: Control<ExtractionReviewFormData>;
  path: FieldPath<ExtractionReviewFormData>;
  /** What the extractor proposed for this path, in draft form. */
  extracted: ExtractionDraftValue;
  /** False on a reviewer-added row, which the extractor never proposed. */
  hasCandidate: boolean;
  /** The extractor's confidence for this path, or null when it gave none. */
  confidence: number | null;
  /**
   * How an answer about this control is remembered — a field key, or a row cell
   * keyed to its extracted row. Not the form path, which renumbers when a row is
   * removed and would move a reviewer's answer to a different cell. Null on a
   * control nothing can be answered about.
   */
  acknowledgeKey: string | null;
  /**
   * What the record this review will be written over holds for this path, or
   * `undefined` when the caller supplied no record at all — which is not the same
   * as a record that holds nothing here.
   */
  recordValue?: ExtractionDraftValue;
  lowConfidenceThreshold: number;
  /**
   * Visible label, inside the input as everywhere else in the app. A repeated
   * column keeps its plain label — the row it belongs to is named by the
   * surrounding group, so nothing has to repeat the row per field.
   */
  label: string;
  disabled: boolean;
}

type FieldRenderProps = {
  field: ControllerRenderProps<ExtractionReviewFormData>;
  fieldState: ControllerFieldState;
};

/**
 * One extracted value, as an always-editable labelled control.
 *
 * Carries the two affordances the document-metadata review established: the
 * shared `SuggestedIcon` sparkle for a value the AI filled that nobody has
 * touched, and — layered on for this surface — a `FieldWarningIndicator` on the
 * end adornment (the slot `reviewDiffAdornment` uses for sources-disagree) when
 * the extractor's confidence was low, or when the value disagrees with what the
 * record already holds.
 *
 * Memoized on its own props, which are all primitives or values stable for the
 * life of the schema. Without it a keystroke re-rendered every control in the
 * dialog: the form-wide `useWatch` behind the attention walk re-renders the whole
 * grid on every change, and a real profile sheet puts 95 of these on screen, each
 * carrying a `Controller` and a MUI input. Its own value still arrives through
 * that `Controller`, so the field the reviewer is typing in updates as it did.
 *
 * Both read `isReviewed`, a comparison against this value's own extracted
 * candidate, rather than `SuggestedField`/`useSuggestedValue`'s dirty check. In a
 * repeated group RHF's dirty state is positional, so removing a row would retire
 * every surviving row's flag; see `isReviewed` for the full reasoning.
 */
export const ExtractionFieldControl = memo(function ExtractionFieldControl({
  spec,
  control,
  path,
  extracted,
  hasCandidate,
  confidence,
  acknowledgeKey,
  recordValue,
  lowConfidenceThreshold,
  label,
  disabled,
}: ExtractionFieldControlProps) {
  const targetRef = useAttentionTarget(path);
  const { isAcknowledged, acknowledge } = useAcknowledgement(acknowledgeKey);
  const { hasArrived, dismiss } = useArrival(path);
  const { markPosition, travelToNextPublished } = useAttentionTravel();

  return (
    <Controller
      name={path}
      control={control}
      render={({ field, fieldState }) => {
        const value: ExtractionDraftValue =
          typeof field.value === "string" || typeof field.value === "boolean" ? field.value : null;
        const reviewed = hasCandidate ? isReviewed(spec, value, extracted) : true;
        const flagged = !isAcknowledged && isFlagged(confidence, reviewed, lowConfidenceThreshold);
        // "The extractor produced something here" — the rule the sparkle and the
        // offered reading both hang off, so they cannot disagree about which
        // fields have a reading behind them.
        const suggested = hasCandidate && !reviewed && !isUnreadDraft(extracted);

        // An answered disagreement stays answered: the reviewer looked at both
        // values and kept this one, so the indicator, the banner count and travel
        // all stop asking. Editing is the other way out, and clears it on its own.
        const conflicted =
          !isAcknowledged &&
          isRecordConflict({ spec, current: value, extracted, recordValue, hasCandidate });
        // Null rather than an empty element when there is nothing to say, so the
        // input's end adornment stays absent instead of rendering a blank slot.
        const warning =
          conflicted || flagged ? (
            <FieldAttention
              spec={spec}
              label={label}
              flagged={flagged}
              confidence={confidence}
              currentValue={value}
              recordValue={conflicted ? recordValue : undefined}
              forceOpen={hasArrived}
            />
          ) : null;

        // Every source that holds something other than what the field holds now.
        //
        // Not "the record, when it conflicts". A conflict is a fact about load
        // time: take the record's value and the conflict is gone, and with it the
        // only route back to what the AI read. Filtering on the current value
        // instead means the two remain interchangeable — taking one immediately
        // offers back the one it replaced.
        const offers = offersDifferingFrom<ExtractionDraftValue>(
          [
            ...(recordValue !== undefined
              ? [
                  {
                    source: "Saved",
                    display: displayValue(spec, recordValue),
                    value: recordValue,
                    tooltip: "What is saved on the record now. Applying this review replaces it.",
                  },
                ]
              : []),
            ...(hasCandidate
              ? [
                  {
                    source: "This scan",
                    display: displayValue(spec, extracted),
                    value: extracted,
                    tooltip: readingHint(confidence),
                  },
                ]
              : []),
          ],
          // Like-typed, so retyping 5 as 5.0 does not offer back a value that is
          // already there — the same comparison the flags use.
          (candidate) => !isReviewed(spec, value, candidate),
          // What can be offered is what was read, not what is worth committing:
          // a checkbox read as unticked is a reading a reviewer must be able to
          // get back to after ticking it.
          isUnreadDraft
        );

        // Blur closes the arrival, but the indicator keeps the tooltip up while the
        // pointer is on it — which is how clicking a value inside it survives the
        // field losing focus. Escape closes it outright.
        const onBlur = () => {
          dismiss();
          field.onBlur();
        };
        // Wherever the reviewer puts themselves is where "jump to next" counts
        // from. Without this it kept counting from the last jump, so clicking into
        // a field halfway down the form and pressing it sent them back up.
        const onFocus = () => markPosition(path);
        // Stopped only when it actually closed something. The review sits in a
        // dialog that closes on Escape, so an unstopped keypress meant for the
        // tooltip discards the whole review — and with it every acknowledgement,
        // which lives in session state. Plain Escape on a field with no tooltip
        // open still closes the dialog, as it did before travel existed.
        const onEscape = (event: KeyboardEvent<HTMLDivElement>) => {
          // Alt+Enter takes the first offer, Alt+n the nth — the same keystrokes
          // the manifest ingest form uses, so the chips are reachable without
          // leaving the caret. The plain matcher rather than the hook, because
          // the offers are only known inside this render callback.
          if (
            takeOfferForEvent(
              event,
              offers,
              (offer) => field.onChange(offer.value),
              conflicted || flagged ? acknowledge : undefined
            )
          ) {
            // Answered from the keyboard, so hand the reviewer to whatever is
            // next rather than leaving them on a field they have just settled.
            // Only from the keyboard: a click already put them somewhere on
            // purpose, and moving the caret out from under a pointer is not the
            // same gesture at all.
            travelToNextPublished();
            return;
          }
          if (event.key !== "Escape" || !hasArrived) return;
          dismiss();
          event.stopPropagation();
        };

        // In the field's own box, under the input, and not gated on focus.
        //
        // An overlay was the wrong primitive twice over: painted below the dialog
        // it was invisible, and on a select it lands exactly where the dropdown
        // opens. In flow it has no stacking order to get wrong and nothing to
        // collide with. Not gated on focus because a reviewer cannot act on help
        // they have to focus each of sixty fields to discover.
        const fieldOffers = (
          <OfferedValues
            offers={offers}
            onTake={(offer) => field.onChange(offer.value)}
            onKeep={conflicted || flagged ? acknowledge : undefined}
            keepValue={displayValue(spec, value)}
            // Only while the field still holds what the extractor read. Once the
            // reviewer has typed over it, the score describes a value that is no
            // longer there, and nothing on the chip would say so.
            keepTooltip={hasCandidate && !reviewed ? confidenceHint(confidence) : undefined}
            ariaLabel={`Other values for ${label}`}
          />
        );

        const body = {
          spec,
          label,
          disabled,
          value,
          suggested,
          warning,
          targetRef,
          onBlur,
          onFocus,
          onEscape,
          fieldOffers,
        };
        if (spec.kind === "boolean") {
          return <BooleanFieldBody {...body} field={field} fieldState={fieldState} />;
        }
        return <TextFieldBody {...body} field={field} fieldState={fieldState} />;
      }}
    />
  );
});

/**
 * The one warning icon a field can carry: either the value disagrees with the
 * record it would replace, or the extractor was unsure of it.
 *
 * One icon, never two. A value can be both, and stacking two warnings on one
 * field says less than one warning that states both — so the disagreement takes
 * the slot and folds the confidence in as a note.
 *
 * The icon explains; it does not act. Acting lives in the offered-value chips
 * under the focused field, which is the same place and the same control the
 * manifest ingest form uses. It used to live in this tooltip, where no keyboard
 * could reach it — on a screen whose whole job is typing.
 */
function FieldAttention({
  spec,
  label,
  flagged,
  confidence,
  currentValue,
  recordValue,
  forceOpen,
}: {
  /** Named through the spec so the tooltip and the chips use the field's own words. */
  spec: ExtractionFieldSpec;
  label: string;
  flagged: boolean;
  confidence: number | null;
  currentValue: ExtractionDraftValue;
  /** Set only when this value would replace a different one on the record. */
  recordValue: ExtractionDraftValue | undefined;
  /** True when travel has just landed here, so the reason shows without a hover. */
  forceOpen: boolean;
}): ReactNode {
  if (recordValue !== undefined) {
    return (
      <FieldWarningIndicator
        tooltip={
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
              This would replace what the record holds
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              Saved: {displayValue(spec, recordValue)}
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              This scan: {displayValue(spec, currentValue)}
            </Typography>
            {flagged && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.7 }}>
                {lowConfidenceNote(confidence)}
              </Typography>
            )}
          </Box>
        }
        ariaLabel={`Differs from the record: ${label}`}
        inline
        forceOpen={forceOpen}
      />
    );
  }
  if (flagged) {
    return (
      <FieldWarningIndicator
        tooltip={lowConfidenceTooltip(confidence)}
        ariaLabel={`Low confidence: ${label}`}
        inline
        forceOpen={forceOpen}
      />
    );
  }
  return null;
}

type FieldBodyProps = FieldRenderProps & {
  spec: ExtractionFieldSpec;
  label: string;
  disabled: boolean;
  value: ExtractionDraftValue;
  suggested: boolean;
  warning: ReactNode;
  /** Registers this control as where travel to its path lands. */
  targetRef: (element: HTMLElement | null) => void;
  onBlur: () => void;
  onFocus: () => void;
  onEscape: (event: KeyboardEvent<HTMLDivElement>) => void;
  /** The offered-value chips, anchored under this field while it is focused. */
  fieldOffers: ReactNode;
};

function BooleanFieldBody({
  label,
  disabled,
  value,
  suggested,
  warning,
  field,
  targetRef,
  onBlur,
  onFocus,
  onEscape,
  fieldOffers,
}: FieldBodyProps) {
  return (
    // Same handlers the text body wires. Without them a checkbox never reports
    // where the reviewer is, so travel counted from the last jump instead, and an
    // arrival on one was never dismissed.
    <Box ref={targetRef} onKeyDown={onEscape} sx={REVEAL_SHORTCUTS_ON_FOCUS}>
      <Stack direction="row" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={value === true}
              onChange={(event) => field.onChange(event.target.checked)}
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={disabled}
              size="small"
            />
          }
          label={label}
        />
        {suggested && <SuggestedIcon isSuggested size="small" />}
        {warning}
      </Stack>
      {fieldOffers}
    </Box>
  );
}

function TextFieldBody({
  spec,
  label,
  disabled,
  value,
  suggested,
  warning,
  field,
  fieldState,
  targetRef,
  onBlur,
  onFocus,
  onEscape,
  fieldOffers,
}: FieldBodyProps) {
  const textValue = typeof value === "string" ? value : "";
  const isTriBoolean = spec.kind === "triBoolean";
  const isSelect = spec.kind === "select" || isTriBoolean;
  // A tri-state's options are fixed; a select's come from the caller. The
  // empty option below is what carries "not answered".
  const options = isTriBoolean ? TRI_BOOLEAN_OPTIONS : (spec.options ?? []);
  // An extractor can emit a value outside the caller's option list.
  const unrecognized = isSelect
    ? unrecognizedOption(textValue, (value) => options.some((option) => option.value === value))
    : null;
  // safeParseFloat nulls anything it can't read; say so rather than
  // discarding what the reviewer typed without comment.
  const unparseableNumber =
    spec.kind === "number" && textValue.trim() !== "" && safeParseFloat(textValue) == null;

  const helperText = (() => {
    if (fieldState.error?.message != null) return fieldState.error.message;
    if (unparseableNumber) return "Enter a number";
    if (unrecognized != null) return "Not one of the expected values — please confirm";
    return spec.helperText;
  })();

  // A select's dropdown arrow is absolutely positioned at the input's trailing
  // edge, so anything in the end adornment lands underneath it. Elsewhere the app
  // sidesteps this by owning the indicator — the pickers are Autocompletes, which
  // render theirs as a flow child and then the extra node after it. A native
  // select cannot do that without giving up arrow-click-to-open, so instead the
  // arrow moves inboard and the adornment keeps the edge, which lands on the same
  // order those pickers produce: indicator, then the warning.
  const adornedSelect = isSelect && (warning != null || spec.unit != null);

  return (
    <Box ref={targetRef} sx={REVEAL_SHORTCUTS_ON_FOCUS}>
      <TextField
        select={isSelect}
        sx={
          adornedSelect
            ? {
                "& .MuiSelect-icon": { right: 30 },
                // Room for both, so a long option does not run under either.
                "& .MuiSelect-select": { pr: 7 },
              }
            : undefined
        }
        multiline={spec.kind === "multiline"}
        minRows={spec.kind === "multiline" ? 2 : undefined}
        size="small"
        fullWidth
        label={label}
        disabled={disabled}
        value={textValue}
        name={field.name}
        inputRef={field.ref}
        // Normalizing "" back to null keeps a cleared field indistinguishable
        // from one the extractor never filled, which is what lets a
        // type-then-delete stop counting as a review. Matches FormTextField.
        onChange={(event) => field.onChange(event.target.value === "" ? null : event.target.value)}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onEscape}
        error={!!fieldState.error || unparseableNumber}
        // The offers sit between the input and whatever the field has to say
        // about itself, so a validation message stays the last thing read — it is
        // the one that says the value is wrong rather than that another is
        // available.
        helperText={
          <>
            {/* Cancels the chip's own label padding, so a chip's text starts
                where the helper text below it starts. Without it the chips read as
                a second, deeper indent — the row is offset from the only other
                line of text in the field. */}
            <Box sx={{ ml: "-8px" }}>{fieldOffers}</Box>
            {helperText}
          </>
        }
        slotProps={{
          // Chips are buttons, and the default helper text is a `<p>`, which
          // cannot legally contain them.
          formHelperText: { component: "div" },
          input: {
            startAdornment: suggested ? <SuggestedAdornment spec={spec} /> : undefined,
            endAdornment:
              spec.unit != null || warning != null ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {spec.unit != null && (
                    <Typography variant="body2" color="text.secondary">
                      {spec.unit}
                    </Typography>
                  )}
                  {warning}
                </Stack>
              ) : undefined,
          },
          htmlInput: {
            inputMode: spec.kind === "number" ? "decimal" : undefined,
          },
        }}
      >
        {isSelect && <MenuItem value="">—</MenuItem>}
        {unrecognized != null && <MenuItem value={unrecognized}>{unrecognized}</MenuItem>}
        {isSelect &&
          options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
      </TextField>
    </Box>
  );
}

function SuggestedAdornment({ spec }: { spec: ExtractionFieldSpec }) {
  return (
    <Box
      sx={{
        display: "flex",
        // A multiline input grows downward and the row centres its children, so
        // the sparkle drifts away from the value it marks. Pinned to the top
        // instead, with the icon's own block margin dropped: the input's padding
        // already places the adornment on the first line, and a 20px icon fills
        // that line's 20.125px box, so any margin on top of it hangs the icon
        // below the line.
        ...(spec.kind === "multiline"
          ? { alignSelf: "flex-start", "& .MuiSvgIcon-root": { my: 0 } }
          : {}),
      }}
    >
      <SuggestedIcon isSuggested size="small" />
    </Box>
  );
}
